/**
 * features/parent/ChildPhotoCard.tsx
 * ------------------------------------------------------------------
 * A parent adds or replaces their child's photograph.
 *
 * WHAT THE PARENT SEES IS WHAT HAPPENS. The photo is not live the moment
 * the upload finishes: the school's server re-draws it first, dropping the
 * location data a phone hides inside every photo. So the card waits for
 * that verdict and says either "done" or the plain reason it was refused —
 * never a spinner that stops without saying anything.
 *
 * CONSENT IS TICKED, NOT ASSUMED. The rules refuse a parent's upload that
 * does not carry the consent version, so the box is the control, not
 * decoration. The words agreed to are versioned and kept with the photo.
 *
 * SEE IT AS THE SCHOOL WILL. Once a photo is chosen, PhotoThumbPreview
 * shows it at the small square sizes the portal uses, cut from the middle
 * as the server will cut it, with a word if it is too wide or too small —
 * so a poor thumbnail is caught before it is sent (Oct 2026).
 *
 * The notice about what the school does with the photo is short and
 * specific on purpose. A parent deciding whether to share a picture of
 * their child is owed the actual answer, not a paragraph of reassurance.
 * ------------------------------------------------------------------ */
import { useRef, useState } from 'react';
import { Camera, ShieldCheck, Loader2, CheckCircle2, AlertTriangle, ImagePlus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { SoftCard, PanelHeader, AccentButton } from '@/features/finance/ui';
import { uploadStudentPhoto, useStudentPhotoUrl } from '@/lib/studentPhoto';
import { uploadFileProblem, PHOTO_CONSENT_TEXT } from '@/lib/studentPhotoPolicy';
import PhotoThumbPreview from '@/components/ui/PhotoThumbPreview';
import type { Student } from '@/types';

export default function ChildPhotoCard({ child }: { child: Student }) {
  const { userData } = useAuth();
  const current = useStudentPhotoUrl(child.photo, 'full');
  const input = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const firstName = child.fullName.split(' ')[0] || child.fullName;

  function choose(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    e.target.value = '';
    setMsg(null);
    if (!f) return;
    const bad = uploadFileProblem(f);
    if (bad) { setMsg({ ok: false, text: bad }); return; }
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }

  function reset() {
    if (preview) URL.revokeObjectURL(preview);
    setFile(null);
    setPreview(null);
    setConsent(false);
  }

  async function send() {
    if (!file || !consent || !userData?.uid) return;
    setBusy(true);
    setMsg(null);
    try {
      const outcome = await uploadStudentPhoto({
        studentId: child.id, file, uploadedBy: userData.uid, consent: true,
      });
      if (outcome.status === 'done') {
        setMsg({ ok: true, text: `${firstName}'s photo has been updated.` });
        reset();
      } else if (outcome.status === 'rejected') {
        setMsg({ ok: false, text: outcome.reason });
      } else {
        setMsg({ ok: true, text: 'Your photo was received and is still being processed. It will appear here shortly.' });
        reset();
      }
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message || 'The photo could not be sent. Please try again.' });
    } finally {
      setBusy(false);
    }
  }

  const shown = preview ?? current;

  return (
    <SoftCard pad="p-0">
      <PanelHeader
        icon={Camera} tone="navy"
        title={`${firstName}'s photo`}
        subtitle={child.photo ? 'You can replace it at any time' : 'No photo yet — you can add one'}
      />
      <div className="p-4 flex flex-col sm:flex-row gap-5">
        <div className="shrink-0">
          {shown ? (
            <img src={shown} alt={child.fullName}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-cover border border-gray-200 dark:border-gray-700" />
          ) : (
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl bg-gray-100 dark:bg-gray-800 border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-400">
              <ImagePlus size={32} />
            </div>
          )}
          {preview && (
            <p className="text-[11px] text-center text-gray-500 mt-1">Not sent yet</p>
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-3">
          <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 text-[13px] text-gray-600 dark:text-gray-300">
            <p className="flex items-start gap-1.5 font-medium text-gray-800 dark:text-gray-100 mb-1">
              <ShieldCheck size={15} className="mt-0.5 shrink-0 text-emerald-600" />
              How the school keeps this photo
            </p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Only school staff and {firstName}'s own parents can see it — it is never shared by link.</li>
              <li>The hidden location data phones store inside photos is removed before it is saved.</li>
              <li>A clear, recent photo of {firstName}'s face works best — no other children in it.</li>
            </ul>
          </div>

          <input ref={input} type="file" accept="image/*" className="hidden" onChange={choose} />

          {!file ? (
            <AccentButton icon={Camera} onClick={() => input.current?.click()} disabled={busy}>
              {child.photo ? 'Choose a new photo' : 'Choose a photo'}
            </AccentButton>
          ) : (
            <>
              {preview && <PhotoThumbPreview src={preview} name={firstName} />}
              <label className="flex items-start gap-2 text-[13px] text-gray-700 dark:text-gray-200 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{PHOTO_CONSENT_TEXT}</span>
              </label>
              <div className="flex flex-wrap gap-2">
                <AccentButton icon={busy ? Loader2 : CheckCircle2} onClick={send} disabled={!consent || busy}>
                  {busy ? 'Sending…' : 'Use this photo'}
                </AccentButton>
                <AccentButton variant="soft" onClick={reset} disabled={busy}>Cancel</AccentButton>
              </div>
            </>
          )}

          {msg && (
            <p className={`text-sm flex items-start gap-1.5 ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
              {msg.ok ? <CheckCircle2 size={15} className="mt-0.5 shrink-0" /> : <AlertTriangle size={15} className="mt-0.5 shrink-0" />}
              {msg.text}
            </p>
          )}
        </div>
      </div>
    </SoftCard>
  );
}
